import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";

const fmt = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export default function AudioSample() {
    const audioRef = useRef(null);
    const barRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
        const audio = new Audio("/assets/prologue.mp3");
        audio.preload = "metadata";
        audioRef.current = audio;

        const onTime = () => setProgress(audio.currentTime);
        const onMeta = () => setDuration(audio.duration);
        const onEnd = () => setPlaying(false);
        audio.addEventListener("timeupdate", onTime);
        audio.addEventListener("loadedmetadata", onMeta);
        audio.addEventListener("ended", onEnd);

        return () => {
            audio.pause();
            audio.removeEventListener("timeupdate", onTime);
            audio.removeEventListener("loadedmetadata", onMeta);
            audio.removeEventListener("ended", onEnd);
        };
    }, []);

    const toggle = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (playing) {
            audio.pause();
            setPlaying(false);
        } else {
            audio.play();
            setPlaying(true);
        }
    };

    const seek = (e) => {
        const audio = audioRef.current;
        const bar = barRef.current;
        if (!audio || !bar || !duration) return;
        const rect = bar.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        audio.currentTime = ratio * duration;
        setProgress(audio.currentTime);
    };

    return (
        <section data-testid="audio-section" className="relative mx-auto max-w-3xl px-6 py-28">
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1 }}
                className="text-center"
            >
                <p className="font-cinzel text-xs tracking-[0.4em] text-[#b89860] uppercase">
                    Listen
                </p>
                <h2 className="font-cinzel mt-6 text-3xl leading-snug text-[#f3e5ab] sm:text-4xl">
                    The Prologue, Read Aloud
                </h2>
                <p className="font-cormorant mx-auto mt-4 max-w-xl text-lg italic text-[#a3b899]">
                    Close your eyes. Let the garden speak.
                </p>

                <div className="mt-12 flex items-center gap-5 rounded-sm border border-[rgba(212,175,55,0.3)] bg-[rgba(15,35,24,0.6)] p-6 backdrop-blur-md sm:gap-7 sm:p-8">
                    <button
                        onClick={toggle}
                        data-testid="audio-play-button"
                        aria-label={playing ? "Pause the prologue" : "Play the prologue"}
                        className="btn-eden flex h-16 w-16 flex-none items-center justify-center rounded-full bg-gradient-to-br from-[rgba(160,120,60,0.95)] to-[rgba(120,85,40,0.95)] text-[#f5e6c8]"
                    >
                        {playing ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
                    </button>

                    <div className="flex-1">
                        <div className="flex items-center justify-between">
                            <p className="font-cinzel text-left text-[0.65rem] tracking-[0.3em] text-[#d4af37] uppercase">
                                From Part One — The Garden
                            </p>
                            <div className={`eq ${playing ? "eq-playing" : ""}`} aria-hidden="true">
                                <span className="eq-bar" />
                                <span className="eq-bar" />
                                <span className="eq-bar" />
                                <span className="eq-bar" />
                                <span className="eq-bar" />
                            </div>
                        </div>
                        <div
                            ref={barRef}
                            onClick={seek}
                            data-testid="audio-progress-bar"
                            className="mt-4 h-1.5 w-full cursor-pointer rounded-full bg-[rgba(212,175,55,0.18)]"
                        >
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-[#b89860] to-[#f3e5ab] transition-[width] duration-200"
                                style={{ width: duration ? `${(progress / duration) * 100}%` : "0%" }}
                            />
                        </div>
                        <div className="font-cormorant mt-2 flex justify-between text-sm italic text-[#8a7a60]">
                            <span data-testid="audio-current-time">{fmt(progress)}</span>
                            <span data-testid="audio-duration">{fmt(duration)}</span>
                        </div>
                    </div>
                </div>

                <p className="font-cormorant mt-6 text-sm italic text-[#8a7a60]">
                    The full audiobook voice is being chosen — this excerpt arrives with the
                    Digital & Audiobook edition at the unveiling.
                </p>
            </motion.div>
        </section>
    );
}
